<template>
  <layout>
    <div
      class="sticky top-0 space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div>
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.personal.account.title") }}
        </h1>
        <p class="text-gray-500 text-sm">
          {{ t("admin.settings.personal.account.description") }}
        </p>
      </div>
    </div>
    <div
      class="flex flex-col space-y-6 p-6 max-w-4xl dynamic-height overflow-auto"
    >
      <Errors />

      <div class="grid sm:grid-cols-3 gap-x-10">
        <span class="col-span-1 text-sm font-medium">
          {{ t("validation.attributes.email") }}
        </span>
        <input
          v-model="details.email"
          type="email"
          name="email"
          id="email"
          class="col-span-2 block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm mt-1 sm:mt-0"
        />
      </div>

      <hr />

      <div class="grid sm:grid-cols-3 gap-x-10">
        <span class="col-span-1 text-sm font-medium">
          {{ t("validation.attributes.new_password") }}
        </span>
        <input
          v-model="details.new_password"
          type="password"
          name="new_password"
          id="new_password"
          class="col-span-2 block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm mt-1 sm:mt-0"
        />
      </div>

      <hr />

      <div class="grid sm:grid-cols-3 gap-x-10">
        <span class="col-span-1 text-sm font-medium">
          {{ t("validation.attributes.new_password_confirmation") }}
        </span>
        <input
          v-model="details.new_password_confirmation"
          type="password"
          name="new_password_confirmation"
          id="new_password_confirmation"
          class="col-span-2 block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm mt-1 sm:mt-0"
        />
      </div>

      <hr />

      <div class="grid sm:grid-cols-3 gap-x-10">
        <span class="col-span-1 text-sm font-medium">
          {{ t("validation.attributes.password") }}
        </span>
        <input
          v-model="details.password"
          type="password"
          name="password"
          id="password"
          class="col-span-2 block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm mt-1 sm:mt-0"
        />
      </div>

      <hr />

      <div class="grid sm:grid-cols-3 gap-x-10">
        <span class="col-span-1 text-sm font-medium">
          {{ t("admin.settings.personal.account.language") }}
        </span>
        <div class="col-span-2">
          <Language
            class="mt-1 sm:mt-0"
            :active="details.locale"
            @language-selected="handleLanguageSelected"
          />
        </div>
      </div>

      <hr />

      <div class="">
        <div>
          <span class="text-sm font-medium">
            {{ t("admin.settings.personal.account.delete_account.title") }}
          </span>
          <p class="text-gray-500 text-sm font-medium">
            {{
              t("admin.settings.personal.account.delete_account.description")
            }}
            <span
              @click="handleDeleteMyAccount"
              class="hover:text-red-700 text-red-500 cursor-pointer"
              >{{
                t(
                  "admin.settings.personal.account.delete_account.delete_button"
                )
              }}.</span
            >
          </p>
        </div>
      </div>

      <hr />

      <div class="flex justify-end">
        <primary-button
          :label="t('components.buttons.save')"
          @click="handleUpdateAccount"
        />
      </div>
    </div>

    <ConfirmationModal
      :open="confirmAccountDeletion"
      @close="confirmAccountDeletion = false"
      @confirm="handleAccountDeletionConfirmed"
      :confirmationLabel="t('admin.settings.account.delete_account.confirm')"
    >
      {{ t("admin.settings.account.delete_account.message") }}
    </ConfirmationModal>

    <trial-countdown />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/UserSettings.vue";
import { usePage } from "@inertiajs/vue3";
import { reactive, ref } from "vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import Language from "@/Tailwind/Components/Language.vue";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";

import { useI18n } from "vue-i18n";
import Errors from "@/Tailwind/Components/Errors.vue";
import TrialCountdown from "@/Tailwind/Components/TrialCountdown.vue";

defineOptions({ layout: PersistentLayout });

const { t } = useI18n();
const i18n = useI18n();

const confirmAccountDeletion = ref(false);
const user = usePage().props.auth.user;
const details = reactive({
  email: user.email,
  locale: user.locale || "en",
  password: null,
  new_password: null,
  new_password_confirmation: null,
});

function handleLanguageSelected(lang) {
  details.locale = lang;
}

function handleDeleteMyAccount() {
  confirmAccountDeletion.value = true;
}

function handleAccountDeletionConfirmed() {
  confirmAccountDeletion.value = false;
  router.delete(route("settings.user.account.delete"));
}

function handleUpdateAccount() {
  router.post(
    route("settings.user.account.update"),
    {
      ...details,
    },
    {
      onSuccess: () => {
        details.password = null;
        details.new_password = null;
        details.new_password_confirmation = null;

        i18n.locale.value = details.locale;
      },
    }
  );
}

function handleBack() {
  router.visit(route("settings.user.personal"));
}
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
